import React, { useEffect, useState } from 'react';
import { inventoryAPI, suppliersAPI, programsAPI } from '@/lib/api';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import { showApiError } from '@/lib/errorHandler';
import { SearchableSelect } from '@/components/SearchableSelect';

export function MiscReceiptForm({ onSuccess, onCancel, loading, setLoading }) {
  const [nonAsSuppliers, setNonAsSuppliers] = useState([]);
  const [allItemsIncNonInv, setAllItemsIncNonInv] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [selectedMiscItem, setSelectedMiscItem] = useState(null);
  const [miscForm, setMiscForm] = useState({
    item_id: '', description: '', supplier_id: '', supplier_name: '',
    quantity: '1', unit_of_measure: 'Nos', cost_per_item: '', total_value: '',
    cost_mode: 'per_item',
    date: new Date().toISOString().split('T')[0],
    reference_number: '', remarks: '', program_id: '',
  });

  useEffect(() => {
    loadNonAsSuppliers();
    loadNonInventoryItems();
    loadPrograms();
  }, []);

  const loadNonAsSuppliers = async () => {
    try {
      const res = await suppliersAPI.list({ supplier_type: 'NON-AS', limit: 500 });
      const list = res.data?.suppliers || res.data || [];
      setNonAsSuppliers(Array.isArray(list) ? list : []);
    } catch { setNonAsSuppliers([]); }
  };

  const loadNonInventoryItems = async () => {
    try {
      const res = await inventoryAPI.listItems({ limit: 500, is_inventory: false, sort_by: 'item_name', sort_order: 'asc' });
      setAllItemsIncNonInv(res.data.items || []);
    } catch { setAllItemsIncNonInv([]); }
  };

  const loadPrograms = async () => {
    try {
      const res = await programsAPI.list();
      setPrograms(res.data || []);
    } catch { setPrograms([]); }
  };

  const handleMiscItemChange = (itemId) => {
    const item = allItemsIncNonInv.find(i => i.id === itemId);
    setSelectedMiscItem(item);
    setMiscForm(f => ({
      ...f,
      item_id: itemId,
      description: item?.description || item?.item_name || '',
      unit_of_measure: item?.unit_of_measure || 'Nos',
    }));
  };

  const handleMiscReceiptSubmit = async (e) => {
    e.preventDefault();
    if (!miscForm.item_id) { toast.error('Please select an item'); return; }
    if (!miscForm.supplier_id) { toast.error('Please select a supplier'); return; }
    if (!miscForm.quantity || parseFloat(miscForm.quantity) <= 0) { toast.error('Quantity must be > 0'); return; }
    if (!miscForm.date) { toast.error('Date is required'); return; }
    const costPerItem = parseFloat(miscForm.cost_per_item) || 0;
    const totalValue = parseFloat(miscForm.total_value) || 0;
    if (miscForm.cost_mode === 'per_item' && costPerItem <= 0) { toast.error('Cost per item is required'); return; }
    if (miscForm.cost_mode === 'total' && totalValue <= 0) { toast.error('Total value is required'); return; }

    setLoading(true);
    try {
      const payload = {
        item_id: miscForm.item_id,
        item_code: selectedMiscItem?.unique_id || '',
        description: miscForm.description,
        supplier_id: miscForm.supplier_id,
        supplier_name: miscForm.supplier_name,
        quantity: parseFloat(miscForm.quantity),
        unit_of_measure: miscForm.unit_of_measure || selectedMiscItem?.unit_of_measure || 'Nos',
        cost_per_item: miscForm.cost_mode === 'per_item' ? costPerItem : null,
        total_value: miscForm.cost_mode === 'total' ? totalValue : null,
        date: miscForm.date,
        reference_number: miscForm.reference_number,
        remarks: miscForm.remarks,
        program_id: miscForm.program_id || '',
      };
      await inventoryAPI.createMiscReceipt(payload);
      toast.success('Misc receipt recorded for spend tracking');
      onSuccess();
    } catch (err) {
      showApiError(err, 'Failed to create misc receipt');
    } finally { setLoading(false); }
  };

  return (
    <>
      <div className="md:col-span-2 border-t pt-3 mt-1">
        <p className="text-xs font-medium text-slate-600 mb-2">Non-Inventory Spend Entry</p>
      </div>
      <div>
        <Label className="text-xs">Supplier (NON-AS) *</Label>
        <SearchableSelect
          options={nonAsSuppliers.map(s => ({
            value: s.id,
            label: s.supplier_name,
            sub: s.supplier_code || s.supplier_id || '',
            searchStr: `${s.supplier_name} ${s.supplier_code || ''} ${s.supplier_id || ''}`,
          }))}
          value={miscForm.supplier_id}
          onSelect={v => {
            const s = nonAsSuppliers.find(sup => sup.id === v);
            setMiscForm(f => ({ ...f, supplier_id: v, supplier_name: s?.supplier_name || '' }));
          }}
          placeholder="Search NON-AS suppliers..."
          emptyText="No NON-AS suppliers found."
          testId="misc-supplier-select"
        />
      </div>
      <div>
        <Label className="text-xs">Date *</Label>
        <Input type="date" value={miscForm.date} onChange={e => setMiscForm(f => ({ ...f, date: e.target.value }))}
          className="h-8 text-xs" data-testid="misc-date" />
      </div>
      <div>
        <Label className="text-xs">Item *</Label>
        <SearchableSelect
          options={allItemsIncNonInv.map(i => ({
            value: i.id,
            label: `${i.unique_id} - ${i.item_name}`,
            sub: `${i.item_type || 'Non-Inventory'} | ${i.unit_of_measure || 'Nos'}`,
            searchStr: `${i.unique_id} ${i.item_name} ${i.description || ''}`,
          }))}
          value={miscForm.item_id}
          onSelect={v => handleMiscItemChange(v)}
          placeholder="Search generic / non-inventory items..."
          emptyText="No non-inventory items found. Create items with 'Is Inventory Item' unchecked."
          testId="misc-item-select"
        />
      </div>
      <div>
        <Label className="text-xs">Description (editable)</Label>
        <Input value={miscForm.description} onChange={e => setMiscForm(f => ({ ...f, description: e.target.value }))}
          placeholder="Enter or modify item description..."
          className="h-8 text-xs" data-testid="misc-description" />
      </div>
      <div>
        <Label className="text-xs">Quantity *</Label>
        <Input type="number" min="0.01" step="any" value={miscForm.quantity}
          onChange={e => setMiscForm(f => ({ ...f, quantity: e.target.value }))}
          className="h-8 text-xs" data-testid="misc-quantity" />
      </div>
      <div>
        <Label className="text-xs">UOM</Label>
        <Input value={miscForm.unit_of_measure} onChange={e => setMiscForm(f => ({ ...f, unit_of_measure: e.target.value }))}
          className="h-8 text-xs" data-testid="misc-uom" />
      </div>
      <div className="md:col-span-2 border-t pt-3">
        <Label className="text-xs mb-1.5 block">Cost Entry *</Label>
        <div className="flex gap-2 mb-2">
          <Button type="button" size="sm" variant={miscForm.cost_mode === 'per_item' ? 'default' : 'outline'}
            onClick={() => setMiscForm(f => ({ ...f, cost_mode: 'per_item', total_value: '' }))}
            className="h-7 text-xs" data-testid="cost-mode-per-item">
            Cost per Item
          </Button>
          <Button type="button" size="sm" variant={miscForm.cost_mode === 'total' ? 'default' : 'outline'}
            onClick={() => setMiscForm(f => ({ ...f, cost_mode: 'total', cost_per_item: '' }))}
            className="h-7 text-xs" data-testid="cost-mode-total">
            Total Value
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {miscForm.cost_mode === 'per_item' ? (
            <div>
              <Label className="text-[10px] text-muted-foreground">Cost per Item (INR) *</Label>
              <Input type="number" min="0" step="0.01" value={miscForm.cost_per_item}
                onChange={e => setMiscForm(f => ({ ...f, cost_per_item: e.target.value }))}
                placeholder="0.00" className="h-8 text-xs" data-testid="misc-cost-per-item" />
              {miscForm.cost_per_item && miscForm.quantity && (
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  Total: INR {(parseFloat(miscForm.cost_per_item) * parseFloat(miscForm.quantity)).toFixed(2)}
                </p>
              )}
            </div>
          ) : (
            <div>
              <Label className="text-[10px] text-muted-foreground">Total Value (INR) *</Label>
              <Input type="number" min="0" step="0.01" value={miscForm.total_value}
                onChange={e => setMiscForm(f => ({ ...f, total_value: e.target.value }))}
                placeholder="0.00" className="h-8 text-xs" data-testid="misc-total-value" />
              {miscForm.total_value && miscForm.quantity && parseFloat(miscForm.quantity) > 0 && (
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  Per item: INR {(parseFloat(miscForm.total_value) / parseFloat(miscForm.quantity)).toFixed(2)}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      <div>
        <Label className="text-xs">Invoice / Bill No.</Label>
        <Input value={miscForm.reference_number} onChange={e => setMiscForm(f => ({ ...f, reference_number: e.target.value }))}
          placeholder="Invoice or bill reference" className="h-8 text-xs" data-testid="misc-reference" />
      </div>
      <div>
        <Label className="text-xs">Link to Program (optional)</Label>
        <SearchableSelect
          options={[
            { value: '_none', label: 'No program', sub: '', searchStr: 'none' },
            ...programs.map(p => ({
              value: p.id,
              label: p.program_name || p.program_number,
              sub: p.program_number || '',
              searchStr: `${p.program_name} ${p.program_number || ''}`,
            }))
          ]}
          value={miscForm.program_id || '_none'}
          onSelect={v => setMiscForm(f => ({ ...f, program_id: v === '_none' ? '' : v }))}
          placeholder="Search programs..."
          emptyText="No programs found."
          testId="misc-program-select"
        />
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs">Remarks</Label>
        <Textarea value={miscForm.remarks} onChange={e => setMiscForm(f => ({ ...f, remarks: e.target.value }))}
          placeholder="Additional notes..." className="text-xs min-h-[50px]" data-testid="misc-remarks" />
      </div>
      {/* Submit buttons for misc receipt */}
      <div className="md:col-span-2 flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="button" disabled={loading} data-testid="save-misc-receipt-btn" onClick={handleMiscReceiptSubmit}>
          <Save className="w-4 h-4 mr-1" />
          {loading ? 'Saving...' : 'Record Misc Receipt'}
        </Button>
      </div>
    </>
  );
}
